import { formatMoney } from '@shopify/theme-currency';
import { handleize } from '../../core/helpers';
import { getSizedImageURL } from '../../core/images';
import variantImages from './variant-images';
import { getAfterpay } from './third-parties';
import dynamicProductExtra from '../../sections/product-page/dynamic-product-extra';

// Look at snippets/product-details.liquid as reference
const getFitsOptions = () => {
  return `
    <div class="product-option" data-option-fit>
      <div class="product-option__head">
        <p class="product-option__title">Fit</p>
      </div>

      <div class="product-option__fits" data-product-option-fits tabindex="0" role="list">
        <div class="product-option__fit" data-product-option-fit data-fit-value="Standard" data-fit-handle="standard" tabindex="0" role="button" aria-label="Standard fit"><span>Standard</span></div>
        <div class="product-option__fit" data-product-option-fit data-fit-value="Plus" data-fit-handle="plus" tabindex="0" role="button" aria-label="Plus fit"><span>Plus</span></div>
      </div>
    </div>
  `;
};

// Look at snippets/product-details.liquid as reference
const getColorOptions = (all_variants, variants_data, swatches_data) => {
  try {
    console.log('swatches_data', swatches_data);

    let html = `
      <div class="product-option" data-quickview-option-color data-option-index="0">
        <div class="product-option__colors" data-quickview-product-option-colors tabindex="0">
    `;

    // Extract the unique colors from the product variants
    const colors = _.uniq(_.map(all_variants, variant => {
      return variant.selectedOptions[0].value;
    }));

    for (let i = 0; i < colors.length; i++) {
      const color = colors[i];
      const color_handle = handleize(color);

      // Make sure at least 1 size is available to show the swatch option
      let total_inventory = 0;
      all_variants.forEach(variant => {
        if (variant.selectedOptions[0].value === color) {
          total_inventory += (variant.astrOnlineInventory === null) ? Number(variant.quantityAvailable) : Number(variant.astrOnlineInventory.value);
        }
      });

      const is_plus_color = (color_handle.includes('plus-size'));
      console.log('Color: ', color, 'Is Plus: ', is_plus_color);

      if (total_inventory <= 0) {
        continue;
      }

      let swatch_span = '<span style="background-color: #FFFFFF;"></span>';
      if (swatches_data.length > 0) {
        swatches_data.find(swatch => {
          if (swatch.color_handle === color_handle) {
            swatch_span = (swatch.swatch_value.length <= 7 && swatch.swatch_value.includes('#') && swatch.swatch_type === 'hex') ? `<span style="background-color:${swatch.swatch_value};"></span>` :
              (swatch.swatch_value === null) ? `<span><img src="https://cdn.shopify.com/s/files/1/2483/6632/files/pixel-transparent_68x68.png?v=1687290419" alt="${color} color swatch" height="34" width="34"></span>` : `<span><img src="${swatch.swatch_value}" alt="${color} color swatch" height="34" width="34"></span>`;
          }
        });
      }

      html += `
        <div class="product-option__color color-swatch" data-variant-id="${_.last(variants_data[0].id.split('/'))}" data-quickview-product-option-color data-color-value="${color}" data-color-handle="${color_handle}" tabindex="0" role="button" aria-label="Color ${color}"${(window.Shopify.shop.includes('staging') ? ' data-staging-swatch' : '')} data-plus-color="${is_plus_color}">${swatch_span}</div>
      `;
    }

    html += `
    </div>
    `;

    return html;
  } catch (error) {
    console.log('[Quickview] Colors options error', error);
    return '';
  }
};

// Look at snippets/product-details.liquid as reference
const getSizesOptions = (all_variants) => {
  try {
    let html = `
      <div class="product-option" data-quickview-option-size data-option-index="0">
        <div class="product-option__sizes" data-quickview-product-option-sizes tabindex="0" role="list">
          <ul role="list" aria-label="{{ 'products.product.size_select' | t }}">
    `;

    const sizes = _.uniq(_.map(all_variants, variant => {
      return variant.selectedOptions[1].value;
    }));
    // console.log('sizes', sizes);

    sizes.forEach(size => {
      const size_handle = handleize(size);

      // html += `
      //   <div class="product-option__size disabled" data-quickview-product-option-size data-size-value="${size}" data-size-handle="${size_handle}" tabindex="0" role="button" aria-label="Size ${size}"><span>${size.toUpperCase()}</span></div>
      // `;

      html += `
      <li role="listitem">
        <button 
          type="button"
          class="product-option__size" 
          data-quickview-product-option-size
          data-size-value="${size}" 
          data-size-handle="${size_handle}" 
          aria-label="Size ${size}">
            <span>${size.toUpperCase()}</span>
        </button>
      </li>
      `;
    });

    html += `
        </ul>
      </div>
    </div>
    `;

    return html;
  } catch (error) {
    console.log('[Quickview] Sizes options error', error);
    return '';
  }
};

const getSizesOptionsList = (all_variants) => {
  try {
    let html = `
      <select name="id" class="no-js" data-quickview-product-select aria-label="Product">
    `;

    all_variants.forEach(variant => {
      const online_inventory = (variant.astrOnlineInventory === null) ? variant.quantityAvailable : Number(variant.astrOnlineInventory.value);
      const variant_id = _.last(variant.id.split('/'));

      const available = (online_inventory > 0) ? '' : ` disabled="disabled"`;
      html += `<option${available} value="${variant_id}" data-inventory-quantity="${online_inventory}"></option>`;
    });

    html += `
      </select>
    `;

    return html;
  } catch (error) {
    console.log('[Quickview] Sizes options list error', error);
    return '';
  }
};

const getSameColorVariants = (selected_color) => {
  try {
    if (window.theme.quickview.product === null) {
      throw new Error('Missing product data');
    }

    // Extract the same color variants
    const same_color_variants_edges = _.filter(window.theme.quickview.product.variants.edges, variant => {
      const has_color = _.find(variant.node.selectedOptions, { name: 'Color', value: selected_color });
      return !(typeof has_color === 'undefined');
    });

    if (same_color_variants_edges.length === 0) {
      throw new Error(`No variants with same color "${selected_color}" found`);
    }

    return same_color_variants_edges;
  } catch (error) {
    console.log('[Quickview] Same color variants error', error);
    return null;
  }
};

// variants_nodes is an array of cleaned up variants done in initProductOptions
const getSameColorSizeVariant = (variants_nodes, selected_color, selected_size) => {
  try {
    if (window.theme.quickview.product === null) {
      throw new Error('Missing product data');
    }

    const variant_edge = _.filter(variants_nodes, variant => {
      if (variant.selectedOptions.length < 2) {
        return false;
      }

      return (variant.selectedOptions[0].value === selected_color && variant.selectedOptions[1].value === selected_size);
    });

    if (variant_edge.length === 0) {
      throw new Error('No variants with same color and size found');
    }

    return variant_edge[0];
  } catch (error) {
    console.log('[Quickview] Same color and size variant error', error);
    return null;
  }
};

// Main product price
const getProductPrice = (current_variant) => {
  try {
    let $html = `
      <div data-price-wrapper>
        <span data-product-price>
          ${formatMoney(Number(current_variant.price.amount) * 100, theme.moneyFormat)}
        </span>
      `;

        if (Number(current_variant.compareAtPrice.amount) > Number(current_variant.price.amount)) {
          $html += `<s data-compare-price>${formatMoney(Number(current_variant.compareAtPrice.amount) * 100, theme.moneyFormat)}</s>`;
        }

        // if (selected_color_name === '') {
        //   // Get the product min price
        //   $html = `<span data-product-price><span class="price-from">From </span>${formatMoney(product.price_min, theme.moneyFormat)}</span>`;
        // }

    $html += `
      </div>
    `;

    return $html;
  } catch (error) {
    console.log('[Quickview] Product price error', error);
    return '';
  }
};

const updateAddToCartStatus = (selected_variant, preselected_size) => {
  if (selected_variant.available) {
    $('[data-quickview-add-to-cart]').prop('disabled', false);
    $('[data-quickview-buy-now]').prop('disabled', false);
    $('[data-quickview-add-to-cart-available]').removeClass('visually-hidden');
    $('[data-quickview-add-to-cart-unavailable]').addClass('visually-hidden');
  } else {
    $('[data-quickview-add-to-cart]').prop('disabled', true);
    $('[data-quickview-buy-now]').prop('disabled', true);
    $('[data-quickview-add-to-cart-available]').addClass('visually-hidden');
    $('[data-quickview-add-to-cart-unavailable]').removeClass('visually-hidden');
  }

  if (preselected_size === '') {
    $('[data-quickview-add-to-cart]').prop('disabled', true);
    $('[data-quickview-buy-now]').prop('disabled', true);

    // Show "select a size" instead of "add to bag" when no size is selected
    $('[data-quickview-add-to-cart-available]').text(window.theme.strings.selectSize);
    $('[data-quickview-add-to-cart-available]').attr('aria-label', window.theme.strings.selectSize);
    $('[data-quickview-add-to-cart]').addClass('need-size');
  }
};

// Init the product options after rendering the initial code
const initProductOptions = (variants, preselected_color = '', preselected_size = '') => {
  try {
    console.log('[Quickview] init variants', variants, ' // preselected_color: "', preselected_color, '" // preselected_size: "', preselected_size, '"');

    if (!variants.length) {
      return;
    }

    const variants_nodes = variants.map(variant => {
      const variant_node = variant.node;
      const online_inventory = (variant_node.astrOnlineInventory === null) ? variant_node.quantityAvailable : Number(variant_node.astrOnlineInventory.value);

      return {
        astrOnlineInventory: online_inventory,
        available: online_inventory > 0,
        compareAtPrice: (variant_node.compareAtPrice === null) ? '' : variant_node.compareAtPrice.amount,
        id: _.last(variant_node.id.split('/')),
        image_url: (variant_node.image === null) ? '' : variant_node.image.url,
        image_alt: (variant_node.image === null) ? '' : variant_node.image.altText,
        price: (variant_node.price === null) ? '' : variant_node.price.amount,
        selectedOptions: variant_node.selectedOptions,
        title: variant_node.title,
      };
    });

    console.log('[Quickview] variants', variants_nodes);

    const selected_color = (preselected_color === '') ? variants_nodes[0].selectedOptions[0].value : preselected_color;
    const selected_color_handle = handleize(selected_color);

    const selected_color_variants = _.filter(variants_nodes, { selectedOptions: [{ name: 'Color', value: selected_color }] });
    console.log('[Quickview] selected_color_variants', selected_color_variants);
    if (!selected_color_variants.length) {
      console.log('[Quickview] No variants found for the selected color', selected_color);
      return;
    }

    // Find the 1st available size when opening the modal or selected size after selecting a size option
    const selected_color_available_variants = _.filter(selected_color_variants, { available: true });
    console.log('[Quickview] selected_color_available_variants', selected_color_available_variants);
    if (!selected_color_available_variants.length) {
      console.log('[Quickview] No available variants for the selected color');
      return;
    }

    if ($(`#quicklook-modal-mobile [data-selected-color-label]`).length) {
      $(`#quicklook-modal-mobile [data-selected-color-label] span`).html(selected_color.replace('Plus Size - ', ''));
    }
    if ($(`#quicklook-modal [data-selected-color-label]`).length) {
      $(`#quicklook-modal [data-selected-color-label] span`).html(selected_color.replace('Plus Size - ', ''));
    }

    let selected_size_variant = null;
    if (preselected_size !== '') {
      selected_size_variant = getSameColorSizeVariant(variants_nodes, preselected_color, preselected_size);
    }
    // console.log('selected_size_variant', selected_size_variant);

    const default_variant = (preselected_size === '') ? selected_color_available_variants[0] : selected_size_variant;
    if (default_variant === null) {
      console.log('[Quickview] No available variants for the selected color and size');
      return;
    }

    console.log('[Quickview] Selected variant', default_variant);

    let quickview_mobile = '';
    if (window.theme.breakpoints.current === 'mobile') {
      quickview_mobile = '-mobile';
    }

    // Handle the disabled state on size options
    $(`[data-quickview-product-option-size]`).addClass('disabled');
    $(`[data-quickview-product-option-size]`).attr('aria-disabled', true);
    $(`[data-quickview-product-option-size]`).attr('aria-label', `Size is ${window.theme.strings.soldOut}`);

    selected_color_available_variants.forEach(variant => {
      const size_button = $(`[data-quickview-product-option-size][data-size-value="${variant.selectedOptions[1].value}"]`);
      size_button.removeClass('disabled');
      size_button.attr('aria-disabled', false);
      size_button.attr('aria-label', `Size ${variant.selectedOptions[1].value}"]`);
    });

    const selected_size = default_variant.selectedOptions[1].value;
    const selected_size_handle = handleize(selected_size);

    console.log('[Quickview] Default variant', default_variant);

    $(`[data-quickview-product-option-color]`).removeClass('active');
    $(`[data-quickview-product-option-size]`).removeClass('active');

    $(`[data-quickview-product-option-color][data-color-handle="${selected_color_handle}"]`).addClass('active');

    // Per request, we're not pre-selecting the 1st size available
    if (preselected_size !== '') {
      $(`[data-quickview-product-option-size][data-size-handle="${selected_size_handle}"]`).addClass('active');
      $(`[data-quickview-product-select]`).val(default_variant.id).trigger('change');
    }

    updateAddToCartStatus(default_variant, preselected_size);

    // Update the product-extra content
    dynamicProductExtra(selected_color, true);

    // Update the "see full details" link & product title link
    const view_details_element = document.querySelector(`[data-quickview${quickview_mobile}-view-details]`);

    if (view_details_element) {
      const base_link = view_details_element.dataset[`quickview${(window.theme.breakpoints.current === 'mobile') ? 'Mobile' : ''}ViewDetailsBaseLink`];
      const color_link = `${base_link}?color=${selected_color}`;

      const view_details_link_element = document.querySelector(`[data-quickview${quickview_mobile}-view-details] a`);
      const desktop_title_element = document.querySelector(`[data-product-scrollable-details] .product-details__title`);
      const mobile_title_element = document.querySelector(`[data-quickview-mobile-details] .quickview-mobile-content__title`);

      if (view_details_link_element) { view_details_link_element.href = color_link; }
      if (desktop_title_element) { desktop_title_element.href = color_link; }
      if (mobile_title_element) { mobile_title_element.href = color_link; }
    }

    // Wishlist
    const event_name = ($('[data-quicklook-modal]').is(':visible')) ? 'quickview:variant-change' : 'quickview-mobile:variant-change';
    const quickview_change = new CustomEvent(event_name, { bubbles: true });
    quickview_change.data = { detail: { variant: default_variant } };
    document.dispatchEvent(quickview_change);
  } catch (error) {
    console.log('[Quickview] Product options initialization error', error);
    return null;
  }
};

const selectColorQuicklook = ($item) => {
  try {
    if ($item.hasClass('active')) {
      return;
    }

    if (window.theme.quickview.product === null) {
      throw new Error('Missing product data');
    }

    const qv_container = ($item.closest('.quickview-modal-mobile').length === 0) ? '#quicklook-modal' : '#quicklook-modal-mobile';
    const edit_mode = $('body').hasClass('edit-cart');

    $(`${qv_container} [data-quickview-product-option-color]`).removeClass('active');
    $item.addClass('active');

    const selected_color_name = $item.data('color-value');
    const same_color_variants_edges = getSameColorVariants(selected_color_name);
    const same_color_variants = same_color_variants_edges.map(edge => edge.node);

    // Replace the images
    if ($item.closest('.quickview-modal-mobile').length === 0) {
      const max_images = (edit_mode) ? 1 : 4;
      const images_html = variantImages(same_color_variants[0], max_images);
      if (images_html !== '') {
        $(`[data-product-scrollable-images]`).html(images_html);
      }
    } else {
      const images_html = variantImages(same_color_variants[0], 1);
      if (images_html !== '') {
        // There's the wishlist button within the image container on mobile
        $('[data-product-main]').remove();
        $(`[data-quickview-mobile-images]`).append(images_html);
      }
    }

    // Reinit the price and Afterpay - All sizes have the same price (all colors as well, but better to be careful since colors are styles)
    const price_html = getProductPrice(same_color_variants[0]);
    const afterpay_html = getAfterpay(same_color_variants[0]);
    $(`${qv_container} [data-price-wrapper]`).remove();
    $(`${qv_container} afterpay-placement`).remove();
    $(`${qv_container} [data-product-pricing]`).append(price_html + afterpay_html);

    // Reinit the sizes
    const sizes_html = getSizesOptions(same_color_variants);
    $(`${qv_container} [data-quickview-option-size]`).html(sizes_html);

    // Reinit options and ctas
    initProductOptions(window.theme.quickview.product.variants.edges, selected_color_name);

    // Disable the update button if it's an edit from the cart
    $('[data-quickview-update]').attr('disabled', true);

    // Update the product-extra content
    dynamicProductExtra(selected_color_name, true);
  } catch (error) {
    console.log('[Quickview] Product color selection error', error);
    return;
  }
};

const selectSizeQuicklook = ($item) => {
  try {
    if ($item.hasClass('active') || $item.hasClass('disabled')) {
      return;
    }

    const qv_container = ($item.closest('.quickview-modal-mobile').length === 0) ? '#quicklook-modal' : '#quicklook-modal-mobile';
    $(`${qv_container} [data-quickview-product-option-size]`).removeClass('active');
    $item.addClass('active');

    const $selected_color_item = $(`${qv_container} [data-quickview-product-option-color].active`);
    if (!$selected_color_item.length) {
      console.log('[Quickview] No color selected.');
      return;
    }
    const selected_color_name = $selected_color_item.data('color-value');
    const selected_size_name = $item.data('size-value');

    // Don't need to refresh the price in this version, all sizes share the same price

    // Reinit options and ctas
    initProductOptions(window.theme.quickview.product.variants.edges, selected_color_name, selected_size_name);

    // Enable the update button if it's an edit from the cart
    $('[data-quickview-update]').removeAttr('disabled');

    // Show "add to bag" instead of "select a size" when a size is selected
    $('[data-quickview-add-to-cart-available]').text(window.theme.strings.addToCart);
    $('[data-quickview-add-to-cart-available]').attr('aria-label', window.theme.strings.addToCart);
    $('[data-quickview-add-to-cart]').removeClass('need-size');
  } catch (error) {
    console.log('[Quickview] Product size selection error', error);
    return;
  }
};

export {
  getFitsOptions,
  getColorOptions,
  getSizesOptions,
  getSizesOptionsList,
  getSameColorVariants,
  getProductPrice,
  initProductOptions,
  selectColorQuicklook,
  selectSizeQuicklook,
};
